import React from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Server, Users, FileText, Eye, ArrowLeft } from "lucide-react";

const SAFEGUARDS = [
  { icon: Lock, title: "Encrypted connection", body: "Information you send to Nauticus travels over an encrypted connection (HTTPS/TLS), so it isn't readable as it moves between your device and our servers." },
  { icon: ShieldCheck, title: "Your password is stored as a one-way hash", body: "We save only a one-way bcrypt hash of your password — never the password itself — so it can be checked when you sign in but cannot be read back by anyone, including our staff." },
  { icon: Eye, title: "Access is checked on every request", body: "Every time your information is opened, our servers confirm the request belongs to your account. Other consumers and unassigned advisors cannot reach your household." },
  { icon: FileText, title: "Documents are kept private", body: "Files like enrollment records are stored in private storage with no public links. They can only be retrieved by you or your authorized advisor through an authenticated request." },
  { icon: Server, title: "The right information, with your permission", body: "You can ask general questions without providing your Medicare number or Social Security number. If you request help checking eligibility or enrolling in coverage, your licensed insurance agent may need additional information, such as your Medicare number. A Social Security number may be needed for certain verification processes, such as checking Medicaid eligibility. Before requesting sensitive information, your licensed insurance agent should explain why it is needed and obtain your permission through the applicable verification or enrollment process. Please do not enter these numbers in the general Ask chat; your advisor will explain the appropriate way to provide them." },
];

function Section({ children }) {
  return <section className="mt-8">{children}</section>;
}

export default function PrivacySecurity() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#0B192C]" data-testid="privacy-security-page">
      <div className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
        <button onClick={() => navigate(-1)} className="text-sm text-[#1E3E62] inline-flex items-center gap-1.5 hover:underline" data-testid="privacy-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-[#1E3E62]">
          <ShieldCheck className="w-4 h-4" /> Privacy &amp; Security
        </div>
        <h1 className="mt-2 font-bold text-3xl sm:text-4xl tracking-tight">How we protect your information</h1>
        <p className="mt-3 text-[#475569] text-base leading-relaxed">
          Nauticus is a technology platform operated by <strong>Navillus Technologies LLC</strong>. Participating
          licensed insurance agents and agencies use Nauticus to help service their clients. Here is plainly what we
          do with your information, who can see it, and the choices you have. We only describe safeguards we actually
          have in place.
        </p>

        <Section>
          <h2 className="font-bold text-lg">What we protect, and how</h2>
          <div className="mt-4 grid gap-3">
            {SAFEGUARDS.map((s) => (
              <div key={s.title} className="flex gap-3 rounded-xl border-2 border-[#E6E8EC] bg-white p-4" data-testid={`safeguard-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <s.icon className="w-5 h-5 text-[#1E3E62] shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">{s.title}</p>
                  <p className="text-sm text-[#475569] mt-0.5 leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <h2 className="font-bold text-lg flex items-center gap-2"><Users className="w-5 h-5 text-[#1E3E62]" /> Who can access your information, and why</h2>
          <ul className="mt-3 space-y-2 text-sm text-[#475569]">
            <li><strong>You</strong> — to view and manage your household.</li>
            <li><strong>Your licensed insurance agent</strong> — the licensed insurance agent assigned to you, to help with your Medicare review and enrollment. If no advisor is assigned yet, one is assigned only when you ask for help.</li>
            <li><strong>Your servicing agency's authorized support staff</strong> — a small number of authorized staff at the agency servicing your account, to provide support.</li>
            <li><strong>Platform service providers</strong> — vetted providers under contract (secure hosting, private file storage, email delivery) that process data only to run the service.</li>
          </ul>
          <p className="mt-3 text-sm text-[#475569]">Signed in, you can see exactly who is assigned to your account on your My Nauticus home screen under “Privacy &amp; who can access your information,” and request a change there.</p>
        </Section>

        <Section>
          <h2 className="font-bold text-lg">Your choices</h2>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed">
            You can ask us to access, correct, or delete your information — use <em>Request an access change</em> in
            My Nauticus, or ask your licensed insurance agent. We action requests through our team; some records may be retained where a law
            or legitimate business need requires it.
          </p>
        </Section>

        <Section>
          <h2 className="font-bold text-lg">What we don't claim</h2>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed">
            We won't overstate our protections. We do not claim to be HIPAA-certified or “bank-level,” and we don't promise
            that every stored copy and backup is encrypted at rest or that data is deleted instantly. If you need specific
            assurances for your situation, ask your licensed insurance agent and we'll tell you what we can and cannot confirm.
          </p>
        </Section>

        <p className="mt-10 text-xs text-[#94A3B8]">Powered by Nauticus · A platform by Navillus Technologies LLC.</p>
      </div>
    </div>
  );
}
